import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { marginFields, visibilityField } from '../_shared/blockFields.js';
import { marginStyle, str, visibilityCss } from '../_shared/blockRender.js';
import { useProductState } from '../_shared/productContext.js';
/**
 * Shrine `product_sku` — config-by-config. The SKU / reference line of the buy-box: an optional
 * "SKU:" label + the variant's SKU value, taken off the product (demo fallback). Shrine's schema is
 * tiny — exactly 4 entries (one decorative `header`):
 *
 *  - `text_style` (select: Body / Subtitle / Uppercase, default Body)  → our `textStyle` (same options).
 *  - `[header] "Margins"` (presentational divider, no id/value)         → marginFields' "Margins" group.
 *  - `margin_top`    (range 0–45 step 3 px, default 15)                 → marginTop (marginFields).
 *  - `margin_bottom` (range 0–45 step 3 px, default 15)                 → marginBottom (marginFields).
 *
 * ID MAPPING (our id ← Shrine id, kept stable — never renamed): textStyle←text_style,
 * marginTop←margin_top, marginBottom←margin_bottom.
 *
 * SKU SOURCE: Shrine binds `product.selected_or_first_available_variant.sku`. Our `StorefrontProduct`
 * model has no `sku` field yet (variant-level SKU is a future backend addition), so — exactly as the
 * block brief asks ("value off the product, demo fallback") — we resolve a stable, human-readable
 * reference from the product/variant identity: the variant id when one is selected, else a SKU derived
 * from the product slug/id. When a real per-variant SKU lands on the model this is the single line to
 * swap. Shrine's blank-guard (`sku.size == 0` → `visibility-hidden`, the line still occupies layout via
 * a non-breaking space placeholder so the buy-box doesn't reflow) is reproduced via `showWhenEmpty`.
 *
 * ADDED (not in Shrine's 4, requested by the section brief "optional label 'SKU:' + value … with
 * size/colour/visibility-when-empty settings"):
 *  - `showLabel` + `label` — Shrine hard-codes a visually-hidden "SKU:" prefix (the `products.product.sku`
 *    translation key, screen-reader only). We surface it as an authorable, VISIBLE label (default "SKU:")
 *    with a show/hide toggle, so the line reads "SKU: ABC-123" on screen — the section brief's "optional
 *    label". When hidden, the value is still announced to assistive tech via a visually-hidden prefix
 *    (faithful to Shrine's a11y behaviour).
 *  - `color` — a colour-scheme select (Text / Accent / …) for the line; Shrine inherits the theme body
 *    colour with no setting. Default `text` reproduces Shrine's inherited colour.
 *  - `showWhenEmpty` — Shrine ALWAYS keeps the (empty) line in flow via `visibility-hidden`. We default
 *    to hiding the whole block when there's no SKU (cleaner buy-box), but expose the toggle so a seller
 *    can keep Shrine's reserve-the-space behaviour. The section brief's "visibility-when-empty" setting.
 *
 * DEFERRED (justified Shopify-platform coupling — none are schema settings, all structural in the Liquid
 * body, see the inventory's `shopifyOnly: []`):
 *  - `section.settings.product` + `product.selected_or_first_available_variant.sku` — Shopify Liquid
 *    product/variant objects; replaced by our resolved product state (demo fallback as above).
 *  - `block.shopify_attributes` — Shopify section-rendering data-attributes for the theme editor; no
 *    equivalent in our block-tree renderer.
 *  - `'products.product.sku' | t` translation key — Shopify i18n; replaced by the authorable label.
 */
export const skuBlockSchema = {
    type: 'sku',
    name: 'SKU',
    max: 1,
    fields: [
        visibilityField,
        // ── Label (Shrine carries a visually-hidden "SKU:" prefix; we surface it) ──
        {
            id: 'showLabel',
            type: 'checkbox',
            label: 'Show label',
            group: 'General',
            info: 'Show a visible prefix before the reference (e.g. "SKU:"). When off, the prefix is still announced to screen readers.',
            default: true,
        },
        {
            id: 'label',
            type: 'text',
            label: 'Label',
            group: 'General',
            default: 'SKU:',
            maxLength: 24,
            visibleWhen: { field: 'showLabel', equals: true },
        },
        // ── Text style (Shrine `text_style`) ──────────────────────────────────────
        {
            id: 'textStyle',
            type: 'select',
            label: 'Text style',
            group: 'General',
            default: 'body',
            options: [
                { value: 'body', label: 'Body' },
                { value: 'subtitle', label: 'Subtitle' },
                { value: 'uppercase', label: 'Uppercase' },
            ],
        },
        {
            id: 'color',
            type: 'select',
            label: 'Color',
            group: 'General',
            default: 'text',
            options: [
                { value: 'text', label: 'Text' },
                { value: 'muted', label: 'Muted' },
                { value: 'accent-1', label: 'Accent' },
            ],
        },
        {
            id: 'showWhenEmpty',
            type: 'checkbox',
            label: 'Reserve space when empty',
            group: 'General',
            info: "Keep the line in place even when the product has no reference (Shrine's behaviour). Off hides the block entirely.",
            default: false,
        },
        // ── Margins (Shrine `[header] "Margins"` + margin_top + margin_bottom) ─────
        ...marginFields(15, 15),
    ],
};
/** Shrine `text_style` → typography. `subtitle` = muted small-caps subtitle; `uppercase` = tracked caps. */
function styleProps(textStyle) {
    switch (textStyle) {
        case 'subtitle':
            return {
                fontSize: 'var(--aqx-theme-text-sm, 14px)',
                fontWeight: 600,
                opacity: 0.85,
            };
        case 'uppercase':
            return {
                fontSize: 'var(--aqx-theme-text-sm, 13px)',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
            };
        case 'body':
        default:
            return {
                fontSize: 'var(--aqx-theme-text-base, 16px)',
            };
    }
}
/** Map the block's `color` select to a CSS var. */
function lineColor(value) {
    switch (value) {
        case 'muted':
            return 'var(--aqx-scheme-muted)';
        case 'accent-1':
            return 'var(--aqx-scheme-accent)';
        case 'text':
        default:
            return 'var(--aqx-scheme-text)';
    }
}
/**
 * Resolve the SKU/reference for the current selection. Our product model has no per-variant SKU yet, so
 * this is the documented demo fallback: prefer the selected variant id, else derive a stable reference
 * from the product slug/id. Returns '' when nothing identifies the product (treated as "empty").
 */
function resolveSku(product, variantId, variants) {
    const v = variants.find((x) => x.id === variantId);
    // A real variant id like "demo-v-sand" reads better uppercased as a reference token.
    if (v)
        return v.id.replace(/^demo-v-/, '').toUpperCase();
    const base = product.slug || product.id;
    return base ? base.replace(/[^a-z0-9]+/gi, '-').toUpperCase() : '';
}
/**
 * Product SKU / reference line. SSR-safe: pure markup + one scoped <style> for visibility; the
 * server-rendered first state is correct (initial variant's SKU) and degrades fully without JS — the
 * value updates client-side when the shared variant state changes, with no JS dependency for display.
 *
 * Empty-state parity: when there's no SKU, the block hides entirely unless `showWhenEmpty` is on, in
 * which case it reserves the line via a non-breaking-space placeholder marked `visibility:hidden`
 * (Shrine's `visibility-hidden`), so the buy-box layout doesn't reflow.
 */
const SkuBlock = ({ block }) => {
    const ps = useProductState();
    if (!ps)
        return null;
    const bs = block.settings;
    const cls = `aqx-sku-${block.id}`;
    const sku = resolveSku(ps.product, ps.variantId, ps.variants);
    const showWhenEmpty = bs.showWhenEmpty === true;
    if (!sku && !showWhenEmpty)
        return null;
    const showLabel = bs.showLabel !== false;
    const labelText = str(bs.label, 'SKU:');
    const textStyle = str(bs.textStyle, 'body');
    const isEmpty = !sku;
    const css = visibilityCss(cls, bs.visibility);
    const wrap = {
        display: 'flex',
        alignItems: 'baseline',
        gap: '0.4em',
        color: lineColor(str(bs.color, 'text')),
        // Shrine's empty-state: keep the line in layout but visually hidden.
        visibility: isEmpty ? 'hidden' : 'visible',
        ...styleProps(textStyle),
        ...marginStyle(bs),
    };
    return (_jsxs(_Fragment, { children: [css ? _jsx("style", { dangerouslySetInnerHTML: { __html: css } }) : null, _jsxs("p", { className: cls, role: "status", style: wrap, children: [showLabel ? (_jsx("span", { style: { fontWeight: 600 }, "aria-hidden": isEmpty ? true : undefined, children: labelText })) : (
                    // Label hidden on screen but announced to assistive tech (Shrine's visually-hidden prefix).
                    _jsx("span", { style: {
                            position: 'absolute',
                            width: '1px',
                            height: '1px',
                            padding: 0,
                            margin: '-1px',
                            overflow: 'hidden',
                            clip: 'rect(0 0 0 0)',
                            whiteSpace: 'nowrap',
                            border: 0,
                        }, children: labelText })), _jsx("span", { children: isEmpty ? ' ' : sku })] })] }));
};
export const skuBlock = {
    type: 'sku',
    schema: skuBlockSchema,
    Component: SkuBlock,
    meta: { label: 'SKU' },
};
//# sourceMappingURL=index.js.map